import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import { Star, User, ShoppingBag, Package, Calendar, MessageSquare, TrendingUp, CheckCircle, ThumbsUp, Loader2 } from 'lucide-react';
import ReviewDialog from '../components/ReviewDialog';
import VerifiedBadge from '../components/VerifiedBadge';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const UserProfile = () => {
  const { userId } = useParams();
  const [profile, setProfile] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [ratingSummary, setRatingSummary] = useState(null);
  const [canReview, setCanReview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reviewsLoading, setReviewsLoading] = useState(true);
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState(null);

  useEffect(() => {
    fetchProfile();
    fetchReviews();
    fetchVerificationStatus();
  }, [userId]);

  const fetchVerificationStatus = async () => {
    try {
      const response = await fetch(`${API_URL}/api/verification/user/${userId}`);
      if (response.ok) {
        const data = await response.json();
        setVerificationStatus(data);
      }
    } catch (error) {
      console.error('Error fetching verification status:', error);
    }
  };

  const fetchProfile = async () => {
    try {
      setLoading(true);
      // Fetch user profile
      const response = await fetch(`${API_URL}/api/user/profile/${userId}`);
      
      if (response.ok) {
        const data = await response.json();
        setProfile(data);
      } else {
        // Fallback to basic profile
        setProfile({
          id: userId,
          name: 'User',
          member_since: new Date().getFullYear(),
          account_type: 'seller'
        });
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      setProfile({
        id: userId,
        name: 'User',
        member_since: new Date().getFullYear(),
        account_type: 'seller'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchReviews = async () => {
    try {
      setReviewsLoading(true);
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const response = await fetch(`${API_URL}/api/reviews/seller/${userId}?limit=10`, { headers });
      
      if (response.ok) {
        const data = await response.json();
        setReviews(data.reviews || []);
        setRatingSummary(data.rating_summary);
        setCanReview(data.can_review);
      }
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setReviewsLoading(false);
    }
  };

  const handleReviewSubmit = () => {
    fetchReviews();
  };

  const handleMarkHelpful = async (reviewId) => {
    const token = localStorage.getItem('token');
    if (!token) {
      alert('Please login to mark reviews as helpful');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/reviews/${reviewId}/helpful`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ helpful: true })
      });
      
      if (response.ok) {
        setReviews(prev => prev.map(r => 
          r.id === reviewId 
            ? { ...r, helpful_count: (r.helpful_count || 0) + 1, marked_helpful: true }
            : r
        ));
      }
    } catch (error) {
      console.error('Error marking helpful:', error);
    }
  };

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;

    for (let i = 0; i < fullStars; i++) {
      stars.push(<Star key={i} className="h-4 w-4 fill-yellow-400 text-yellow-400" />);
    }
    if (hasHalfStar) {
      stars.push(<Star key="half" className="h-4 w-4 fill-yellow-400 text-yellow-400 opacity-50" />);
    }
    const emptyStars = 5 - Math.ceil(rating);
    for (let i = 0; i < emptyStars; i++) {
      stars.push(<Star key={`empty-${i}`} className="h-4 w-4 text-gray-300" />);
    }
    return stars;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-12">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center space-x-4">
              <Skeleton className="w-20 h-20 rounded-full bg-white/20" />
              <div className="flex-1">
                <Skeleton className="h-8 w-48 bg-white/20 mb-2" />
                <Skeleton className="h-4 w-32 bg-white/20" />
              </div>
            </div>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <Skeleton className="h-64 w-full" />
              <Skeleton className="h-96 w-full" />
            </div>
            <div>
              <Skeleton className="h-48 w-full" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Profile not found</p>
      </div>
    );
  }

  const averageRating = ratingSummary?.average_rating || 0;
  const totalReviews = ratingSummary?.total_reviews || 0;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center space-x-4">
            <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center">
              {profile.profile_image ? (
                <img 
                  src={profile.profile_image} 
                  alt={profile.name}
                  className="w-20 h-20 rounded-full object-cover"
                />
              ) : (
                <User className="h-10 w-10 text-blue-600" />
              )}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-3xl font-bold">{profile.name}</h1>
                {verificationStatus?.verification_level && verificationStatus.verification_level !== 'none' && (
                  <VerifiedBadge level={verificationStatus.verification_level} size="md" />
                )}
              </div>
              <div className="flex items-center space-x-4 text-sm">
                <Badge variant="secondary" className="bg-white/20 text-white border-0">
                  Seller
                </Badge>
                <div className="flex items-center space-x-1">
                  <Calendar className="h-4 w-4" />
                  <span>Member since {profile.member_since || new Date().getFullYear()}</span>
                </div>
                {totalReviews > 0 && (
                  <div className="flex items-center space-x-1">
                    <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                    <span>{averageRating.toFixed(1)} ({totalReviews} reviews)</span>
                  </div>
                )}
              </div>
            </div>
            {canReview?.can_review && (
              <Button 
                onClick={() => setShowReviewDialog(true)} 
                className="bg-white text-blue-600 hover:bg-gray-100"
              >
                Leave Review
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Rating Cards */}
          <div className="lg:col-span-2 space-y-6">
            {/* Seller Rating Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Package className="h-5 w-5 text-blue-600" />
                  <span>Seller Rating</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {reviewsLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="text-center">
                      <div className="text-5xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">
                        {averageRating.toFixed(1)}
                      </div>
                      <div className="flex justify-center space-x-1 mb-2">
                        {renderStars(averageRating)}
                      </div>
                      <p className="text-sm text-gray-500">
                        Based on {totalReviews} reviews
                      </p>
                      {ratingSummary?.verified_reviews > 0 && (
                        <p className="text-xs text-green-600 flex items-center justify-center gap-1 mt-1">
                          <CheckCircle className="h-3 w-3" />
                          {ratingSummary.verified_reviews} verified
                        </p>
                      )}
                    </div>
                    
                    {/* Rating Distribution */}
                    <div className="space-y-2">
                      {[5, 4, 3, 2, 1].map(r => {
                        const count = ratingSummary?.rating_distribution?.[r.toString()] || 0;
                        const percentage = totalReviews > 0 ? (count / totalReviews) * 100 : 0;
                        return (
                          <div key={r} className="flex items-center gap-2 text-sm">
                            <span className="w-3">{r}</span>
                            <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                            <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-yellow-400 rounded-full transition-all"
                                style={{ width: `${percentage}%` }}
                              />
                            </div>
                            <span className="w-8 text-gray-500 text-right">{count}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Reviews Section */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <MessageSquare className="h-5 w-5" />
                  <span>Reviews</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {reviewsLoading ? (
                  <div className="space-y-4">
                    <Skeleton className="h-24 w-full" />
                    <Skeleton className="h-24 w-full" />
                    <Skeleton className="h-24 w-full" />
                  </div>
                ) : reviews.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <MessageSquare className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                    <p>No reviews yet</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {reviews.map((review) => (
                      <div key={review.id} className="border rounded-lg p-4 hover:bg-gray-50 transition-colors">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            <div className="w-10 h-10 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full flex items-center justify-center">
                              {review.reviewer_image ? (
                                <img 
                                  src={review.reviewer_image} 
                                  alt={review.reviewer_name}
                                  className="w-10 h-10 rounded-full object-cover"
                                />
                              ) : (
                                <User className="h-5 w-5 text-blue-600" />
                              )}
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <p className="font-semibold">{review.reviewer_name}</p>
                                {review.verified_contact && (
                                  <Badge variant="secondary" className="text-xs bg-green-100 text-green-700">
                                    <CheckCircle className="h-3 w-3 mr-1" />
                                    Verified
                                  </Badge>
                                )}
                              </div>
                              <p className="text-xs text-gray-500">
                                {new Date(review.created_at).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-1 mb-2">
                          {renderStars(review.rating)}
                          <span className="ml-2 text-sm font-semibold">{review.rating}</span>
                        </div>
                        {review.title && (
                          <h4 className="font-medium mb-1">{review.title}</h4>
                        )}
                        <p className="text-gray-700">{review.content || review.comment}</p>
                        
                        <div className="flex items-center gap-4 text-sm mt-3">
                          <button
                            onClick={() => handleMarkHelpful(review.id)}
                            disabled={review.marked_helpful}
                            className={`flex items-center gap-1 transition-colors ${
                              review.marked_helpful 
                                ? 'text-blue-600' 
                                : 'text-gray-500 hover:text-blue-600'
                            }`}
                          >
                            <ThumbsUp className="h-4 w-4" />
                            <span>Helpful ({review.helpful_count || 0})</span>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Activity Stats</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Active Listings:</span>
                  <span className="font-semibold">{profile.active_listings || 0}</span>
                </div>
                <Separator />
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Total Reviews:</span>
                  <span className="font-semibold">{totalReviews}</span>
                </div>
                <Separator />
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Verified Reviews:</span>
                  <span className="font-semibold">{ratingSummary?.verified_reviews || 0}</span>
                </div>
              </CardContent>
            </Card>

            {averageRating >= 4 && totalReviews >= 5 && (
              <Card className="bg-gradient-to-br from-blue-50 to-purple-50 border-blue-200">
                <CardContent className="p-6">
                  <div className="flex items-center space-x-2 mb-3">
                    <TrendingUp className="h-5 w-5 text-blue-600" />
                    <h3 className="font-semibold text-blue-900">Top Rated Seller</h3>
                  </div>
                  <div className="flex items-center gap-2 mb-2">
                    <div className="flex">{renderStars(averageRating)}</div>
                    <span className="text-xl font-bold text-blue-600">{averageRating.toFixed(1)}</span>
                  </div>
                  <p className="text-sm text-blue-700">
                    This seller is highly trusted in the community
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>

      <ReviewDialog
        open={showReviewDialog}
        sellerId={userId}
        sellerName={profile?.name}
        onClose={() => setShowReviewDialog(false)}
        onSubmit={handleReviewSubmit}
      />
    </div>
  );
};

export default UserProfile;
